import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { Search, X, ChevronDown, Folder, Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
} from '@/components/ui/popover';
import { routes } from 'routes/routes';
import { getFromLocalStorage } from 'services/localstorage';
import constants from 'utils/constants';
import { cn } from '@/lib/utils';
import {
  getTextAfterSearchInLibrarySectionClick,
  getLibrarySearchType,
  getLibrarySearchValue,
  getNullableNumberFromString,
  getNullableDateFromString,
  getEncodedUriComponentFromObject,
  removeBlankAttributes,
  getNumberInUSALocaleOrDefaultString,
} from './utils';
import {
  SEARCH_IN_FOLDERS_TEXT,
  SEARCH_IN_FILMS_TEXT,
  advancedSearchFilmsQueryFields,
  advancedSearchDateFormat,
  genres,
} from './constants';

interface BudgetValue {
  min: string;
  max: string;
}

interface SearchFormFieldValues {
  searchFilmsInputValue: string;
  advancedSearchStartDate: string;
  advancedSearchEndDate: string;
  advancedSearchStartDateInputValue: string;
  advancedSearchEndDateInputValue: string;
  checkedGenres: Record<string, boolean>;
  productionBudgetValue: BudgetValue;
}

interface LocationState {
  searchFormFieldValues?: SearchFormFieldValues;
  encodedQueryParams?: string;
}

interface SearchFieldProps {
  isHeader: boolean;
}

const SearchField: React.FC<SearchFieldProps> = ({ isHeader }) => {
  const history = useHistory();
  const location = useLocation<LocationState>();

  const [searchFilmsConfigDropdownOpen, setSearchFilmsConfigDropdownOpen] =
    useState(false);

  const [searchFilmsInputValue, setSearchFilmsInputValue] = useState('');

  const searchFilmsInputRef = useRef<HTMLInputElement>(null);

  const [advancedSearchEndDate, setAdvancedSearchEndDate] = useState('');
  const [advancedSearchStartDate, setAdvancedSearchStartDate] = useState('');
  const [advancedSearchEndDateInputValue, setAdvancedSearchEndDateInputValue] =
    useState('');
  const [
    advancedSearchStartDateInputValue,
    setAdvancedSearchStartDateInputValue,
  ] = useState('');

  const [checkedGenres, setCheckedGenres] = useState<Record<string, boolean>>(
    {},
  );

  const defaultBudgetValue: BudgetValue = {
    min: '',
    max: '',
  };

  const [productionBudgetValue, setProductionBudgetValue] =
    useState<BudgetValue>(defaultBudgetValue);

  const clearSearchFilmsInput = () => {
    setSearchFilmsInputValue('');
  };

  const handleSearchInLibrarySectionClick = (
    searchInLibrarySectionText: string,
  ) => {
    const updatedSearchFilmsInputValue =
      getTextAfterSearchInLibrarySectionClick(
        searchInLibrarySectionText,
        searchFilmsInputValue,
      );

    setSearchFilmsInputValue(updatedSearchFilmsInputValue);
    setSearchFilmsConfigDropdownOpen(false);

    searchFilmsInputRef.current?.focus();
  };

  const resetAdvancedSearchFields = () => {
    setAdvancedSearchStartDate('');
    setAdvancedSearchStartDateInputValue('');
    setAdvancedSearchEndDate('');
    setAdvancedSearchEndDateInputValue('');
    setSearchFilmsInputValue('');
    setCheckedGenres({});
    setProductionBudgetValue(defaultBudgetValue);
  };

  const handleProductionBudgetChange = (value: string, type: 'min' | 'max') => {
    const formattedValue = getNumberInUSALocaleOrDefaultString(value);
    const updatedProductionBudget = {
      ...productionBudgetValue,
      [type]: formattedValue,
    };
    setProductionBudgetValue(updatedProductionBudget);
  };

  const createSearchQueryParams = () => {
    let searchQueryParams: any = {
      [advancedSearchFilmsQueryFields.advanced_search]: {
        [advancedSearchFilmsQueryFields.production_budget]: {
          min: getNullableNumberFromString(
            productionBudgetValue.min.replace(/,/g, ''),
          ),
          max: getNullableNumberFromString(
            productionBudgetValue.max.replace(/,/g, ''),
          ),
        },
      },
    };

    searchQueryParams = removeBlankAttributes(searchQueryParams);

    const librarySearchValue = getLibrarySearchValue(searchFilmsInputValue);
    if (librarySearchValue) {
      searchQueryParams[advancedSearchFilmsQueryFields.library_search] = {
        value: librarySearchValue,
      };

      const librarySearchType = getLibrarySearchType(searchFilmsInputValue);
      if (librarySearchType) {
        searchQueryParams[advancedSearchFilmsQueryFields.library_search].type =
          librarySearchType;
      }
    }

    const dateRangeStart = getNullableDateFromString(
      advancedSearchStartDate,
      advancedSearchDateFormat,
    );
    if (dateRangeStart) {
      searchQueryParams[advancedSearchFilmsQueryFields.advanced_search][
        advancedSearchFilmsQueryFields.date_range
      ] = {
        start: dateRangeStart,
      };
    }

    const dateRangeEnd = getNullableDateFromString(
      advancedSearchEndDate,
      advancedSearchDateFormat,
    );
    if (dateRangeEnd) {
      const dateRangeObj =
        searchQueryParams[advancedSearchFilmsQueryFields.advanced_search][
          advancedSearchFilmsQueryFields.date_range
        ];

      searchQueryParams[advancedSearchFilmsQueryFields.advanced_search][
        advancedSearchFilmsQueryFields.date_range
      ] = {
        ...dateRangeObj,
        end: dateRangeEnd,
      };
    }

    searchQueryParams[advancedSearchFilmsQueryFields.advanced_search][
      advancedSearchFilmsQueryFields.genre
    ] = genres.filter((genre) => !!checkedGenres[genre]);

    return searchQueryParams;
  };

  const getSearchFormFieldValues = (): SearchFormFieldValues => {
    const searchFormFieldValues: SearchFormFieldValues = {
      searchFilmsInputValue,
      advancedSearchStartDate,
      advancedSearchEndDate,
      advancedSearchStartDateInputValue,
      advancedSearchEndDateInputValue,
      checkedGenres,
      productionBudgetValue,
    };

    return searchFormFieldValues;
  };

  const compareSearchFormFieldsWithLastSearchFromAdvancedSearchPage = (
    searchFormFieldValues: SearchFormFieldValues,
    lastSearchAdvancedSearchPageFieldValues: SearchFormFieldValues,
  ) =>
    searchFormFieldValues.searchFilmsInputValue ===
      lastSearchAdvancedSearchPageFieldValues.searchFilmsInputValue &&
    searchFormFieldValues.advancedSearchStartDate ===
      lastSearchAdvancedSearchPageFieldValues.advancedSearchStartDate &&
    searchFormFieldValues.advancedSearchEndDate ===
      lastSearchAdvancedSearchPageFieldValues.advancedSearchEndDate &&
    searchFormFieldValues.advancedSearchStartDateInputValue ===
      lastSearchAdvancedSearchPageFieldValues.advancedSearchStartDateInputValue &&
    searchFormFieldValues.advancedSearchEndDateInputValue ===
      lastSearchAdvancedSearchPageFieldValues.advancedSearchEndDateInputValue &&
    searchFormFieldValues.productionBudgetValue.min ===
      lastSearchAdvancedSearchPageFieldValues.productionBudgetValue.min &&
    searchFormFieldValues.productionBudgetValue.max ===
      lastSearchAdvancedSearchPageFieldValues.productionBudgetValue.max &&
    !genres.some(
      (genre) =>
        searchFormFieldValues.checkedGenres[genre] !==
        lastSearchAdvancedSearchPageFieldValues.checkedGenres[genre],
    );

  const handleSearchFormSubmit = (event?: React.FormEvent) => {
    if (event) event.preventDefault();

    // Close popover synchronously before navigation
    flushSync(() => {
      setSearchFilmsConfigDropdownOpen(false);
    });

    const searchQueryParams = createSearchQueryParams();

    const encodedQueryParams =
      getEncodedUriComponentFromObject(searchQueryParams);

    const searchFormFieldValues = getSearchFormFieldValues();

    const locationState: LocationState = {
      searchFormFieldValues,
      encodedQueryParams,
    };

    history.push(`${routes.SEARCH_RESULTS}`, locationState);
    history.go(0);
  };

  useEffect(() => {
    if (location.state) {
      const { searchFormFieldValues } = location.state;

      if (searchFormFieldValues) {
        setSearchFilmsInputValue(searchFormFieldValues.searchFilmsInputValue);
        setAdvancedSearchStartDate(
          searchFormFieldValues.advancedSearchStartDate,
        );
        setAdvancedSearchEndDate(searchFormFieldValues.advancedSearchEndDate);
        setAdvancedSearchStartDateInputValue(
          searchFormFieldValues.advancedSearchStartDateInputValue,
        );
        setAdvancedSearchEndDateInputValue(
          searchFormFieldValues.advancedSearchEndDateInputValue,
        );
        setCheckedGenres(searchFormFieldValues.checkedGenres);
        setProductionBudgetValue(searchFormFieldValues.productionBudgetValue);
      }
    }
  }, [location]);

  const handleMoreOptionsLinkClick = (event: React.MouseEvent) => {
    event.preventDefault();

    const searchFormFieldValues = getSearchFormFieldValues();

    const lastSearchAdvancedSearchPageFieldValues = getFromLocalStorage(
      constants.advancedSearchPageLastSearchLocalStorageKey,
      constants.rememberAdvancedSearchPageLastSearchTimeout,
    );

    const fieldsAreSame = lastSearchAdvancedSearchPageFieldValues
      ? compareSearchFormFieldsWithLastSearchFromAdvancedSearchPage(
          searchFormFieldValues,
          lastSearchAdvancedSearchPageFieldValues,
        )
      : false;

    const advancedSearchPageLocationState = {
      advancedSearchPageFieldValuesFromSearchForm: fieldsAreSame
        ? null
        : searchFormFieldValues,
    };

    setSearchFilmsConfigDropdownOpen(false);
    history.push(`${routes.ADVANCED_SEARCH}`, advancedSearchPageLocationState);
  };

  const openSearchForm = () => {
    setSearchFilmsConfigDropdownOpen(true);
    // Keep focus on input after dropdown opens
    setTimeout(() => {
      searchFilmsInputRef.current?.focus();
    }, 0);
  };

  return (
    <form
      className="tw-w-full md:tw-w-5/6 lg:tw-w-2/3 xl:tw-w-7/12 tw-px-6"
      onReset={() => resetAdvancedSearchFields()}
      onSubmit={handleSearchFormSubmit}
    >
      <Popover
        open={searchFilmsConfigDropdownOpen}
        onOpenChange={(open) => {
          setSearchFilmsConfigDropdownOpen(open);
        }}
        modal={false}
      >
        <div className="tw-relative">
          <PopoverAnchor asChild>
            <div
              id="search-field-container"
              className="tw-flex tw-items-center tw-bg-white tw-rounded-md tw-border tw-border-gray-300 tw-shadow-sm hover:tw-border-gray-400 focus-within:tw-border-blue-500 focus-within:tw-ring-1 focus-within:tw-ring-blue-500 tw-transition-colors"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="tw-text-gray-500 hover:tw-text-gray-700 tw-shrink-0"
                    onClick={() => handleSearchFormSubmit()}
                  >
                    <Search className="tw-h-5 tw-w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Search</TooltipContent>
              </Tooltip>

              <div
                className="tw-flex-1 tw-cursor-text"
                onClick={() => searchFilmsInputRef.current?.focus()}
              >
                <Input
                  type="text"
                  name="search"
                  id="search"
                  placeholder="Search film titles, actors, directors, producers, genre, budget and more..."
                  className={cn(
                    'tw-border-0 tw-flex-1 tw-bg-transparent focus-visible:tw-ring-0 focus-visible:tw-ring-offset-0 tw-text-lg tw-p-0 tw-h-auto tw-leading-normal tw-cursor-text',
                    isHeader ? 'tw-text-left' : 'tw-text-center',
                  )}
                  onChange={(e) => setSearchFilmsInputValue(e.target.value)}
                  onFocus={openSearchForm}
                  value={searchFilmsInputValue}
                  ref={searchFilmsInputRef}
                  autoComplete="off"
                />
              </div>

              {searchFilmsInputValue.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="reset"
                      variant="ghost"
                      size="icon"
                      className="tw-text-gray-500 hover:tw-text-gray-700"
                      onClick={() => clearSearchFilmsInput()}
                    >
                      <X className="tw-h-5 tw-w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Clear search</TooltipContent>
                </Tooltip>
              )}

              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="tw-text-gray-500 hover:tw-text-gray-700"
                >
                  <ChevronDown className="tw-h-5 tw-w-5" />
                </Button>
              </PopoverTrigger>
            </div>
          </PopoverAnchor>

          <PopoverContent
            align="start"
            className="tw-w-[--radix-popper-anchor-width] tw-p-2"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
            }}
            onCloseAutoFocus={(e) => {
              e.preventDefault();
            }}
            onInteractOutside={(e) => {
              // Don't close popover when clicking on the search field
              const target = e.target as HTMLElement;
              if (target.closest('#search-field-container')) {
                e.preventDefault();
              }
            }}
          >
            <div className="tw-text-xs tw-font-semibold tw-mb-2 tw-px-2 tw-text-gray-500">
              Search in Library:
            </div>
            <button
              onClick={() =>
                handleSearchInLibrarySectionClick(SEARCH_IN_FOLDERS_TEXT)
              }
              className="tw-flex tw-w-full tw-items-center tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-sm hover:tw-bg-accent hover:tw-text-accent-foreground tw-cursor-pointer"
            >
              <Folder className="tw-mr-2 tw-h-4 tw-w-4" />
              Folders
            </button>
            <button
              onClick={() =>
                handleSearchInLibrarySectionClick(SEARCH_IN_FILMS_TEXT)
              }
              className="tw-flex tw-w-full tw-items-center tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-sm hover:tw-bg-accent hover:tw-text-accent-foreground tw-cursor-pointer"
            >
              <Film className="tw-mr-2 tw-h-4 tw-w-4" />
              Films
            </button>
            <div className="tw-my-1 tw-h-px tw-bg-gray-200" />
            <button
              onClick={handleMoreOptionsLinkClick}
              className="tw-flex tw-w-full tw-items-center tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-sm tw-font-bold hover:tw-bg-accent hover:tw-text-accent-foreground tw-cursor-pointer"
            >
              Advanced Search
            </button>
          </PopoverContent>
        </div>
      </Popover>
      <input
        type="submit"
        className="tw-invisible tw-w-px tw-h-px tw-absolute"
        onClick={(event) => handleSearchFormSubmit(event)}
      />
    </form>
  );
};

export default SearchField;
